<script setup>
import { ref, onMounted, computed, defineExpose } from 'vue';
import { useStorage } from '@/composables/use-storage';
import axios from 'axios';

const { userInfo } = useStorage();

const storage = ref({
    countFiles: 0,
    using: 0,
    max: 0,
    isFull: false
});

const refreshStorageData = async () => {
    if (userInfo.value?.id) {
        try {
            const response = await axios.get(`/api/sellers/${userInfo.value.id}/storage`);
            storage.value = response.data;
        } catch (error) {
            console.error('Error fetching storage info:', error);
        }
    }
};

onMounted(() => {
    refreshStorageData();
});

const percentUsed = computed(() => {
    if (storage.value.max === 0) return 0;
    return Math.min(100, Math.round((storage.value.using / storage.value.max) * 100));
});

const storageColorClass = computed(() => {
    if (percentUsed.value > 90) return 'almost-full';
    if (percentUsed.value >= 50) return 'half-full';
    return '';
});

const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const remainingStorage = computed(() => {
    return formatBytes(storage.value.max - storage.value.using);
});

defineExpose({ refreshStorageData });
</script>

<template>
    <div class="storage-counter">
        <div class="storage-header">
            <span class="storage-title">Storage</span>
            <span class="storage-percentage" :class="storageColorClass">
                {{ percentUsed }}%
            </span>
        </div>
        
        <div class="storage-bar">
            <div class="storage-bar-filled" :style="{ width: percentUsed + '%' }" :class="storageColorClass"></div>
        </div>
        
        <div class="storage-stats">
            <div class="stat-item">
                <span class="stat-label">Used</span>
                <span class="stat-value">{{ formatBytes(storage.using) }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Free</span>
                <span class="stat-value">{{ remainingStorage }}</span>
            </div>
            <div class="stat-item">
                <span class="stat-label">Files</span>
                <span class="stat-value">{{ storage.countFiles }}</span>
            </div>
        </div>
        
        <div v-if="storage.isFull" class="storage-warning">
            Storage full!
        </div>
    </div>
</template>

<style scoped lang="postcss">
.storage-counter {
    @apply p-3 rounded-xl bg-echoes-background-gray/30 border border-echoes-border-gray text-sm w-full max-w-[360px];
}

.storage-header {
    @apply flex justify-between items-center mb-2;
}

.storage-title {
    @apply text-sm font-semibold text-white;
}

.storage-percentage {
    @apply text-xs font-semibold text-emerald-600 dark:text-emerald-400;
}

.storage-percentage.half-full {
    @apply text-amber-600 dark:text-amber-400;
}

.storage-percentage.almost-full {
    @apply text-red-600 dark:text-red-400;
}

.storage-bar {
    @apply h-1 w-full bg-echoes-background-gray rounded overflow-hidden mb-2;
}

.storage-bar-filled {
    @apply h-full bg-emerald-600 dark:bg-emerald-500 rounded transition-all duration-300 ease-in-out;
}

.storage-bar-filled.half-full {
    @apply bg-amber-600 dark:bg-amber-500;
}

.storage-bar-filled.almost-full {
    @apply bg-red-600 dark:bg-red-500;
}

.storage-stats {
    @apply grid grid-cols-3 gap-2 mt-2 text-xs;
}

.stat-item {
    @apply flex flex-col;
}

.stat-label {
    @apply text-xs text-white/60 mb-0.5;
}

.stat-value {
    @apply text-xs font-medium text-white whitespace-nowrap;
}

.storage-warning {
    @apply mt-2 py-1.5 px-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs font-medium text-center;
}
</style>
